import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSocket } from '../hooks/useSocket.ts';
import { C2S, S2C } from '../multiplayer/events.ts';
import type { RoomInfo, ChallengeReceived, ChallengeConfig, RematchConfig, RematchInvite } from '../multiplayer/types.ts';
import { ToastContainer } from '../components/ToastNotification.tsx';
import type { Unlock } from '@ricochet/achievements';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RoomToast {
  kind: 'room';
  id: string;
  roomId: string;
  roomName: string;
  hostUsername: string;
  hostCountry: string;
  playerCount: number;
  maxPlayers: number;
  gridSize: 8 | 16;
  format: 'FT3' | 'FT5';
}

export interface AchievementToast {
  kind: 'achievement';
  id: string;
  /** The achievement registry id — used to resolve the specific icon. */
  achievementId: string;
  name: string;
  description: string;
  tierLabel: string;
  rewardAvatar?: string;
}

export interface ChallengeToast {
  kind: 'challenge';
  id: string;
  challengeId: string;
  fromUsername: string;
  fromCountry: string;
  fromAvatar: string | null;
  config: ChallengeConfig;
}

export interface RematchToast {
  kind: 'rematch';
  id: string;
  roomId: string;
  originalGameId: string;
  config: RematchConfig;
  fromUsername: string;
  fromCountry: string;
  fromAvatar: string | null;
}

export interface InfoToast {
  kind: 'info';
  id: string;
  message: string;
}

export type AppToast = RoomToast | AchievementToast | ChallengeToast | RematchToast | InfoToast;

interface NotificationContextValue {
  /** Number of rooms with status 'waiting' or 'playing' */
  activeRoomCount: number;
  toasts: AppToast[];
  dismissToast: (id: string) => void;
  /** Surface REST-driven achievement unlocks through the same toast UI. */
  pushAchievementToasts: (unlocks: Unlock[]) => void;
}

// ── Context ───────────────────────────────────────────────────────────────────

const NotificationContext = createContext<NotificationContextValue>({
  activeRoomCount: 0,
  toasts: [],
  dismissToast: () => {},
  pushAchievementToasts: () => {},
});

export function useNotifications(): NotificationContextValue {
  return useContext(NotificationContext);
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { socket } = useSocket();
  const location = useLocation();
  const navigate = useNavigate();

  const [rooms, setRooms] = useState<RoomInfo[]>([]);
  const [toasts, setToasts] = useState<AppToast[]>([]);

  // Refs so socket handlers can read latest values without re-subscribing
  const locationRef = useRef(location.pathname);
  const initialFetchDoneRef = useRef(false);
  /** Timestamp (ms) of the last toast that was actually shown. */
  const lastToastAtRef = useRef(0);

  useEffect(() => { locationRef.current = location.pathname; }, [location.pathname]);

  const pushToast = useCallback((room: RoomInfo) => {
    // Suppress while user is already watching the lobby
    if (locationRef.current.startsWith('/multiplayer')) return;
    // Rate-limit: at most one toast per 10 seconds
    const now = Date.now();
    if (now - lastToastAtRef.current < 10_000) return;
    lastToastAtRef.current = now;

    const id = `${room.id}-${now}`;
    setToasts((prev) => {
      // Keep at most 3 toasts stacked at once
      const next = prev.slice(-2);
      return [
        ...next,
        {
          kind: 'room',
          id,
          roomId: room.id,
          roomName: room.name,
          hostUsername: room.hostUsername,
          hostCountry: room.hostCountry,
          playerCount: room.playerCount,
          maxPlayers: room.maxPlayers,
          gridSize: room.gridSize,
          format: room.format,
        },
      ];
    });
  }, []);

  const pushAchievementToasts = useCallback((unlocks: Unlock[]) => {
    if (unlocks.length === 0) return;
    setToasts((prev) => {
      const now = Date.now();
      const newToasts: AchievementToast[] = unlocks.map((u, i) => ({
        kind: 'achievement',
        id: `ach-${u.achievementId}-${u.tier}-${now}-${i}`,
        achievementId: u.achievementId,
        name: u.def.name,
        description: u.def.tiers[u.tier - 1]?.description ?? u.def.description,
        tierLabel: u.def.tiers[u.tier - 1]?.label ?? '',
        rewardAvatar: u.rewardAvatar,
      }));
      // Keep at most 3 toasts stacked at once
      return [...prev, ...newToasts].slice(-3);
    });
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    if (!socket) return;

    // Fetch initial room list to seed the active-room count
    socket.emit(C2S.LOBBY_LIST);

    const handleRoomList = (list: RoomInfo[]) => {
      initialFetchDoneRef.current = true;
      setRooms(list);
    };

    const handleRoomUpdated = (update: {
      roomId: string;
      playerCount: number;
      status: string;
    }) => {
      setRooms((prev) => {
        const idx = prev.findIndex((r) => r.id === update.roomId);
        if (idx === -1) return prev; // will be refreshed by next poll
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          playerCount: update.playerCount,
          status: update.status as RoomInfo['status'],
        };
        return updated;
      });
    };

    // Server broadcasts full RoomInfo to all lobby-channel clients when a
    // room is first created. Triggers the toast and updates the bubble count.
    const handleRoomCreated = (room: RoomInfo) => {
      setRooms((prev) => {
        if (prev.some((r) => r.id === room.id)) return prev;
        return [...prev, room];
      });
      if (initialFetchDoneRef.current) {
        pushToast(room);
      }
    };

    const handleAchievementUnlocked = (unlocks: Unlock[]) => {
      pushAchievementToasts(unlocks);
    };

    // Challenges are directed at this user specifically, so they bypass the
    // generic in-app-notifications toggle (the opt-out is "accept challenges",
    // enforced server-side).
    const handleChallengeReceived = (data: ChallengeReceived) => {
      setToasts((prev) => [
        ...prev,
        {
          kind: 'challenge' as const,
          id: `challenge-${data.challengeId}`,
          challengeId: data.challengeId,
          fromUsername: data.from.username,
          fromCountry: data.from.country,
          fromAvatar: data.from.avatarFilename,
          config: data.config,
        },
      ].slice(-3));
    };

    // Remove a pending challenge toast (declined-by-them is for the challenger;
    // cancelled = expired/disconnected is for the challengee).
    const dismissChallenge = (challengeId: string) => {
      setToasts((prev) => prev.filter((t) => !(t.kind === 'challenge' && t.challengeId === challengeId)));
    };

    const handleChallengeDeclined = ({ challengeId, fromUsername }: { challengeId: string; fromUsername?: string }) => {
      dismissChallenge(challengeId);
      const message = fromUsername ? `${fromUsername} declined your challenge.` : 'Challenge declined.';
      setToasts((prev) => [...prev, { kind: 'info' as const, id: `info-${Date.now()}`, message }].slice(-3));
    };

    const handleChallengeCancelled = ({ challengeId }: { challengeId: string }) => {
      dismissChallenge(challengeId);
    };

    // The challenger may have navigated away from the lobby while the invite was
    // pending. When the target accepts, the server creates the room and tells us
    // its id — jump straight into it so both players land in the match together.
    const handleChallengeAccepted = ({ challengeId, roomId }: { challengeId: string; roomId: string }) => {
      dismissChallenge(challengeId);
      navigate(`/multiplayer/${roomId}`);
    };

    const handleChallengeFailed = ({ reason }: { reason: string }) => {
      setToasts((prev) => [...prev, { kind: 'info' as const, id: `info-${Date.now()}`, message: reason }].slice(-3));
    };

    // A player from a just-finished game clicked Rematch and opened a new room.
    // Deduped by originalGameId so a second clicker can't stack a duplicate toast.
    const handleRematchInvite = (data: RematchInvite) => {
      setToasts((prev) => {
        const id = `rematch-${data.originalGameId}`;
        if (prev.some((t) => t.id === id)) return prev;
        return [
          ...prev,
          {
            kind: 'rematch' as const,
            id,
            roomId: data.roomId,
            originalGameId: data.originalGameId,
            config: data.config,
            fromUsername: data.from.username,
            fromCountry: data.from.country,
            fromAvatar: data.from.avatarFilename,
          },
        ].slice(-3);
      });
    };

    socket.on(S2C.LOBBY_ROOM_LIST, handleRoomList);
    socket.on(S2C.LOBBY_ROOM_UPDATED, handleRoomUpdated);
    socket.on(S2C.LOBBY_ROOM_CREATED, handleRoomCreated);
    socket.on(S2C.ACHIEVEMENT_UNLOCKED, handleAchievementUnlocked);
    socket.on(S2C.CHALLENGE_RECEIVED, handleChallengeReceived);
    socket.on(S2C.CHALLENGE_ACCEPTED, handleChallengeAccepted);
    socket.on(S2C.CHALLENGE_DECLINED, handleChallengeDeclined);
    socket.on(S2C.CHALLENGE_CANCELLED, handleChallengeCancelled);
    socket.on(S2C.CHALLENGE_FAILED, handleChallengeFailed);
    socket.on(S2C.REMATCH_INVITE, handleRematchInvite);

    // Periodic refresh keeps the bubble count accurate (e.g. rooms created
    // before this client connected, or rooms that ended).
    const poll = setInterval(() => socket.emit(C2S.LOBBY_LIST), 60_000);

    return () => {
      socket.off(S2C.LOBBY_ROOM_LIST, handleRoomList);
      socket.off(S2C.LOBBY_ROOM_UPDATED, handleRoomUpdated);
      socket.off(S2C.LOBBY_ROOM_CREATED, handleRoomCreated);
      socket.off(S2C.ACHIEVEMENT_UNLOCKED, handleAchievementUnlocked);
      socket.off(S2C.CHALLENGE_RECEIVED, handleChallengeReceived);
      socket.off(S2C.CHALLENGE_ACCEPTED, handleChallengeAccepted);
      socket.off(S2C.CHALLENGE_DECLINED, handleChallengeDeclined);
      socket.off(S2C.CHALLENGE_CANCELLED, handleChallengeCancelled);
      socket.off(S2C.CHALLENGE_FAILED, handleChallengeFailed);
      socket.off(S2C.REMATCH_INVITE, handleRematchInvite);
      clearInterval(poll);
    };
  }, [socket, pushToast, pushAchievementToasts, navigate]);

  const activeRoomCount = rooms.filter(
    (r) => r.status === 'waiting' || r.status === 'playing'
  ).length;

  return (
    <NotificationContext.Provider value={{ activeRoomCount, toasts, dismissToast, pushAchievementToasts }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </NotificationContext.Provider>
  );
}
